import { Monitor, Pencil, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { weeklyHours } from '../../lib/schedule';
import { endpoints, type Screen, type ScreenPatch, type ScreenSchedule, type ScreenSchedules, type SharedCanvas } from '../../services/api';
import { useScreen } from '../../store/screen';
import { useSession } from '../../store/session';
import { useSettings } from '../../store/settings';
import { ghostButtonClass, inputClass, Section } from './controls';

const ONLINE_MS = 4 * 60_000;

/** How long each layout stays on screen in a rotation. */
export const ROTATION_INTERVALS = [
  { sec: 60, name: '1 minute' },
  { sec: 300, name: '5 minutes' },
  { sec: 600, name: '10 minutes' },
  { sec: 900, name: '15 minutes' },
  { sec: 1800, name: '30 minutes' },
  { sec: 3600, name: '1 hour' },
  { sec: 7200, name: '2 hours' },
  { sec: 14400, name: '4 hours' },
];

function lastSeen(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < ONLINE_MS) return 'Online now';
  const min = Math.round(ms / 60_000);
  if (min < 60) return `Last seen ${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `Last seen ${h} h ago`;
  return `Last seen ${new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;
}

/** The schedule a screen follows (its own, else the one for all screens); null = always on. */
export function resolveSchedule(scheduleId: string | null, all: ScreenSchedules): ScreenSchedule | null {
  const id = scheduleId ?? all.defaultId;
  if (!id || id === 'always') return null;
  return all.schedules.find((s) => s.id === id) ?? null;
}

export const scheduleSummary = (s: ScreenSchedule | null): string => (s ? `${s.name} (${weeklyHours(s)} h a week)` : 'Always on');

/** Settings → Screens: every screen showing KCL Canvas for this account, what each shows and when. */
export function ScreensTab({ go }: { go: (tab: string) => void }) {
  const { config, user } = useSession();
  const presets = useSettings((s) => s.presets);
  const myId = useScreen((s) => s.deviceId);
  const setPlan = useScreen((s) => s.setPlan);
  const [screens, setScreens] = useState<Screen[] | null>(null);
  const [schedules, setSchedules] = useState<ScreenSchedules>({ schedules: [], defaultId: null });
  const [canvases, setCanvases] = useState<SharedCanvas[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    endpoints
      .screens()
      .then((r) => {
        setScreens(r.devices);
        setSchedules({ schedules: r.schedules, defaultId: r.defaultId });
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load your screens'));
  }, []);
  useEffect(() => {
    if (!user) return;
    load();
    endpoints
      .canvases()
      .then((r) => setCanvases(r.canvases))
      .catch(() => undefined);
    const t = window.setInterval(load, 30_000);
    return () => window.clearInterval(t);
  }, [user, load]);

  if (!config?.features.accounts) return <p className="muted text-sm">Screens need accounts, which aren’t switched on for this site yet.</p>;
  if (!user) return <p className="muted text-sm">Sign in (Settings → Account) to see and manage every screen showing KCL Canvas.</p>;

  const update = async (s: Screen, patch: ScreenPatch) => {
    const next = { ...s, ...patch };
    setScreens((list) => list?.map((x) => (x.deviceId === s.deviceId ? next : x)) ?? list);
    // This screen changes straight away; others pick it up when they next check in.
    if (s.deviceId === myId) {
      setPlan(user.id, { presetId: next.presetId, rotation: next.rotation, schedule: resolveSchedule(next.scheduleId, schedules) });
    }
    await endpoints.updateScreen(s.deviceId, patch).catch(() => load());
  };
  const remove = async (s: Screen) => {
    setScreens((list) => list?.filter((x) => x.deviceId !== s.deviceId) ?? list);
    if (s.deviceId === myId) setPlan(user.id, { presetId: null, rotation: null, schedule: null });
    await endpoints.removeScreen(s.deviceId).catch(() => load());
  };

  const ordered = [...(screens ?? [])].sort((a, b) => Number(b.deviceId === myId) - Number(a.deviceId === myId));
  const options: ShowOption[] = [
    { id: 'main', name: 'Main setup' },
    ...presets.map((p) => ({ id: p.id, name: `Saved layout: ${p.name}` })),
    ...canvases.map((c) => ({ id: `shared:${c.id}`, name: `Shared: ${c.name}${c.owner.me ? '' : ` (from ${c.owner.name || c.owner.email})`}` })),
  ];

  return (
    <>
      <Section
        title="Your screens"
        description="Every TV, tablet and computer showing KCL Canvas while signed in to this account. Choose what each one shows – your main setup, a saved layout, or several in turn – and when it’s on. Changes made on a screen showing a saved layout are saved into that layout."
      >
        {error && <p className="text-sm text-red-400">{error}</p>}
        {!screens && !error && <p className="muted text-sm">Loading…</p>}
        <ul className="space-y-2">
          {ordered.map((s) => (
            <ScreenRow
              key={s.deviceId}
              screen={s}
              isThis={s.deviceId === myId}
              options={options}
              schedules={schedules}
              onChange={(patch) => void update(s, patch)}
              onRemove={() => void remove(s)}
            />
          ))}
        </ul>
        <p className="muted text-xs">
          {presets.length === 0 && (
            <>
              To show something different on a screen, save a setup first in{' '}
              <button type="button" className="text-[var(--accent)] hover:underline" onClick={() => go('presets')}>
                Appearance → Saved layouts
              </button>
              .{' '}
            </>
          )}
          On/off times are set in{' '}
          <button type="button" className="text-[var(--accent)] hover:underline" onClick={() => go('schedules')}>
            Screens → Schedules
          </button>
          .
        </p>
      </Section>
      <p className="muted text-xs">A screen appears here once it has been signed in to your account. To sign a TV in, use Settings → Account → Sign in with your phone.</p>
    </>
  );
}

interface ShowOption {
  /** 'main', a saved layout id, or 'shared:<canvas id>'. */
  id: string;
  name: string;
}

const ROTATE = '__rotate';

function ScreenRow({
  screen: s,
  isThis,
  options,
  schedules,
  onChange,
  onRemove,
}: {
  screen: Screen;
  isThis: boolean;
  options: ShowOption[];
  schedules: ScreenSchedules;
  onChange: (patch: ScreenPatch) => void;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(s.name);
  const [confirm, setConfirm] = useState(false);
  // Choosing "several in turn" opens the picker; it's saved once two or more are ticked.
  const [rotating, setRotating] = useState(Boolean(s.rotation));
  const [picked, setPicked] = useState<string[]>(s.rotation?.items ?? [s.presetId ?? 'main']);
  const [interval, setIntervalSec] = useState(s.rotation?.intervalSec ?? 300);
  useEffect(() => {
    if (s.rotation) {
      setRotating(true);
      setPicked(s.rotation.items);
      setIntervalSec(s.rotation.intervalSec);
    }
  }, [s.rotation]);

  const online = Date.now() - new Date(s.lastSeen).getTime() < ONLINE_MS;
  const size = s.info?.screen ? `${s.info.screen.width}×${s.info.screen.height}` : null;
  const current = s.presetId ?? 'main';
  const missing = !options.some((o) => o.id === current);
  const allDefault = resolveSchedule(null, schedules);

  const saveRotation = (items: string[], intervalSec: number) => {
    setPicked(items);
    setIntervalSec(intervalSec);
    // Keep the chosen order of the list; two or more needed.
    const ordered = options.map((o) => o.id).filter((id) => items.includes(id));
    if (ordered.length >= 2) onChange({ rotation: { items: ordered, intervalSec } });
  };

  return (
    <li className="space-y-2 rounded-lg border border-[var(--border)] p-3 text-sm">
      <div className="flex items-center gap-3">
        <Monitor className="h-5 w-5 shrink-0 opacity-80" aria-hidden />
        <div className="min-w-0 flex-1">
          {editing ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim()) onChange({ name: name.trim() });
                setEditing(false);
              }}
            >
              <input className={inputClass} value={name} maxLength={60} autoFocus onChange={(e) => setName(e.target.value)} onBlur={() => setEditing(false)} aria-label="Screen name" />
            </form>
          ) : (
            <div className="flex items-center gap-2">
              <span className="truncate font-medium">{s.name}</span>
              {isThis && <span className="shrink-0 rounded-full bg-[var(--accent)] px-2 py-0.5 text-xs text-black">This screen</span>}
              <button type="button" onClick={() => setEditing(true)} aria-label={`Rename ${s.name}`} className="opacity-60 hover:opacity-100">
                <Pencil className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          )}
          <div className="muted truncate text-xs">
            <span className={online ? 'text-emerald-400' : ''}>{lastSeen(s.lastSeen)}</span>
            {' · '}
            {s.kind}
            {size ? ` · ${size}` : ''}
            {s.info?.showing ? ` · showing ${s.info.showing}` : ''}
          </div>
        </div>
        {confirm ? (
          <button className={`${ghostButtonClass} border-red-400 text-red-400`} onClick={onRemove}>
            Remove?
          </button>
        ) : (
          <button className={ghostButtonClass} onClick={() => setConfirm(true)} aria-label={`Remove ${s.name}`}>
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>

      <label className="flex items-center gap-3">
        <span className="w-20 shrink-0">Shows</span>
        <select
          className={`${inputClass} bg-[var(--surface-strong)]`}
          value={rotating ? ROTATE : current}
          onChange={(e) => {
            if (e.target.value === ROTATE) {
              setRotating(true);
              return;
            }
            setRotating(false);
            onChange({ presetId: e.target.value === 'main' ? null : e.target.value, rotation: null });
          }}
          aria-label={`What ${s.name} shows`}
        >
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
          {missing && !rotating && <option value={current}>A layout that’s gone (shows the main setup)</option>}
          <option value={ROTATE} disabled={options.length < 2}>
            Several in turn…
          </option>
        </select>
      </label>

      {rotating && (
        <div className="ml-[5.75rem] space-y-2 rounded-lg bg-black/10 p-3">
          <ul className="space-y-1.5">
            {options.map((o) => (
              <li key={o.id}>
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    className="accent-[var(--accent)]"
                    checked={picked.includes(o.id)}
                    onChange={() => saveRotation(picked.includes(o.id) ? picked.filter((p) => p !== o.id) : [...picked, o.id], interval)}
                  />
                  {o.name}
                </label>
              </li>
            ))}
          </ul>
          <label className="flex items-center gap-3">
            <span className="shrink-0">Each for</span>
            <select
              className={`${inputClass} bg-[var(--surface-strong)]`}
              value={interval}
              onChange={(e) => saveRotation(picked, Number(e.target.value))}
              aria-label="How long each one is shown"
            >
              {ROTATION_INTERVALS.map((i) => (
                <option key={i.sec} value={i.sec}>
                  {i.name}
                </option>
              ))}
            </select>
          </label>
          {picked.length < 2 ? (
            <p className="text-xs text-amber-400">Tick at least two to rotate between them.</p>
          ) : (
            <p className="muted text-xs">Screens with the same choices change together, in the order listed.</p>
          )}
        </div>
      )}

      <label className="flex items-center gap-3">
        <span className="w-20 shrink-0">Schedule</span>
        <select
          className={`${inputClass} bg-[var(--surface-strong)]`}
          value={s.scheduleId ?? ''}
          onChange={(e) => onChange({ scheduleId: e.target.value || null })}
          aria-label={`When ${s.name} is on`}
        >
          <option value="">Same as all screens – {scheduleSummary(allDefault)}</option>
          <option value="always">Always on</option>
          {schedules.schedules.map((sc) => (
            <option key={sc.id} value={sc.id}>
              {scheduleSummary(sc)}
            </option>
          ))}
          {s.scheduleId && s.scheduleId !== 'always' && !schedules.schedules.some((sc) => sc.id === s.scheduleId) && (
            <option value={s.scheduleId}>A schedule that’s gone (always on)</option>
          )}
        </select>
      </label>
    </li>
  );
}
